import { useQueryClient } from "@tanstack/react-query";
import type React from "react";
import { useCallback } from "react";
import { deleteNode } from "@/api/knowledgeGraphApi";
import type { VisualNode } from "@/types/knowledgeGraph";

interface UseGraphMutationsOptions {
	graphNodes: VisualNode[];
	selectedNodeId: string | null;
	selectedGraphId: string | null;
	setSelectedGraphId: React.Dispatch<React.SetStateAction<string | null>>;
	setSelectedNodeId: React.Dispatch<React.SetStateAction<string | null>>;
	deleteNodeInGraph: (nodeId: string) => void;
}

interface UseGraphMutationsReturn {
	handleDeleteNode: (nodeId: string) => Promise<void>;
	handleGraphGenerated: (graphId: string) => void;
}

export const useGraphMutations = ({
	graphNodes,
	selectedNodeId,
	selectedGraphId,
	setSelectedGraphId,
	setSelectedNodeId,
	deleteNodeInGraph,
}: UseGraphMutationsOptions): UseGraphMutationsReturn => {
	const queryClient = useQueryClient();

	const handleDeleteNode = useCallback(
		async (nodeId: string) => {
			if (!selectedGraphId) {
				console.error("No graph selected for node deletion");
				return;
			}
			const node = graphNodes.find((candidateNode) => candidateNode.id === nodeId);
			if (!node) {
				console.error("Node not found for deletion:", nodeId);
				return;
			}

			try {
				await deleteNode(selectedGraphId, nodeId);
				deleteNodeInGraph(nodeId);
				if (selectedNodeId === nodeId) {
					setSelectedNodeId(null);
				}
			} catch (error) {
				console.error("Failed to delete node:", error);
				alert(`Failed to delete node: ${error instanceof Error ? error.message : "Unknown error"}`);
			}
		},
		[graphNodes, deleteNodeInGraph, selectedNodeId, selectedGraphId, setSelectedNodeId],
	);

	const handleGraphGenerated = useCallback(
		(graphId: string) => {
			setSelectedGraphId(graphId);
			queryClient.invalidateQueries({ queryKey: ["knowledgeGraphs"] });
			queryClient.invalidateQueries({ queryKey: ["knowledgeGraphVisualization", graphId] });
		},
		[setSelectedGraphId, queryClient],
	);

	return {
		handleDeleteNode,
		handleGraphGenerated,
	};
};

export default useGraphMutations;
